import React from 'react';

interface AddExpenseFormProps {
  newExpense: {
    item: string;
    category: 'Initial Cost' | 'Maintenance';
    localCost: string;
    foreignCost: string;
    date: string;
    note: string;
    personInCharge: string;
  };
  setNewExpense: React.Dispatch<
    React.SetStateAction<{
      item: string;
      category: 'Initial Cost' | 'Maintenance';
      localCost: string;
      foreignCost: string;
      date: string;
      note: string;
      personInCharge: string;
    }>
  >;
  isAddingExpense: boolean;
  handleAddExpense: (e: React.FormEvent) => Promise<void>;
  expenseReceiptRef: React.RefObject<HTMLInputElement>;
  itemInputRef: React.RefObject<HTMLInputElement>;
  editingExpenseId: string | null;
  currency: string;
  onClose?: () => void;
}

export function AddExpenseForm({
  newExpense,
  setNewExpense,
  isAddingExpense,
  handleAddExpense,
  editingExpenseId,
  expenseReceiptRef,
  itemInputRef,
  currency,
  onClose,
}: AddExpenseFormProps) {
  return (
    <div className="">
      <form onSubmit={handleAddExpense} className="space-y-4">
        <div className="grid grid-cols-1 gap-3">
          <input
            ref={itemInputRef}
            required
            placeholder="Item (e.g. Repairs)"
            className="w-full rounded-xl border border-zinc-200 bg-white px-4 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-500 dark:border-zinc-800 dark:bg-zinc-900 dark:text-white"
            value={newExpense.item}
            onChange={(e) =>
              setNewExpense({ ...newExpense, item: e.target.value })
            }
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <select
            required
            className="w-full rounded-xl border border-zinc-200 bg-white px-4 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-500 dark:border-zinc-800 dark:bg-zinc-900 dark:text-white"
            value={newExpense.category}
            onChange={(e) =>
              setNewExpense({
                ...newExpense,
                category: e.target.value as 'Initial Cost' | 'Maintenance',
              })
            }
          >
            <option value="Maintenance">Maintenance</option>
            <option value="Initial Cost">Initial Cost</option>
          </select>

          <input
            placeholder="Note (Optional)"
            className="w-full rounded-xl border border-zinc-200 bg-white px-4 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-500 dark:border-zinc-800 dark:bg-zinc-900 dark:text-white"
            value={newExpense.note}
            onChange={(e) =>
              setNewExpense({ ...newExpense, note: e.target.value })
            }
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <input
            type="number"
            required
            placeholder={`Cost (${currency})`}
            className="w-full rounded-xl border border-zinc-200 bg-white px-4 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-500 dark:border-zinc-800 dark:bg-zinc-900 dark:text-white"
            value={newExpense.localCost}
            onChange={(e) =>
              setNewExpense({ ...newExpense, localCost: e.target.value })
            }
          />
          <input
            type="number"
            placeholder="Cost (Foreign)"
            className="w-full rounded-xl border border-zinc-200 bg-white px-4 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-500 dark:border-zinc-800 dark:bg-zinc-900 dark:text-white"
            value={newExpense.foreignCost}
            onChange={(e) =>
              setNewExpense({ ...newExpense, foreignCost: e.target.value })
            }
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <input
            type="date"
            required
            className="w-full rounded-xl border border-zinc-200 bg-white px-4 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-500 dark:border-zinc-800 dark:bg-zinc-900 dark:text-white"
            value={newExpense.date}
            onChange={(e) =>
              setNewExpense({ ...newExpense, date: e.target.value })
            }
          />
          <input
            placeholder="C/O (Person in charge)"
            className="w-full rounded-xl border border-zinc-200 bg-white px-4 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-500 dark:border-zinc-800 dark:bg-zinc-900 dark:text-white"
            value={newExpense.personInCharge}
            onChange={(e) =>
              setNewExpense({ ...newExpense, personInCharge: e.target.value })
            }
          />
        </div>
        <div className="grid grid-cols-1 gap-3">
          <div className="relative">
            <input
              ref={expenseReceiptRef}
              type="file"
              accept="image/*,application/pdf"
              className="w-full text-[10px] file:mr-2 file:rounded-lg file:border-0 file:bg-zinc-100 file:px-2 file:py-1 file:text-[10px] file:font-bold file:text-zinc-600 dark:file:bg-zinc-800 dark:file:text-zinc-400"
            />
          </div>
        </div>
        <div className="flex gap-3">
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              className="flex-1 rounded-xl bg-zinc-100 py-3 text-xs font-bold tracking-wider text-zinc-600 uppercase transition-colors hover:bg-zinc-200 dark:bg-zinc-800 dark:text-zinc-400 dark:hover:bg-zinc-700"
            >
              Cancel
            </button>
          )}
          <button
            type="submit"
            disabled={isAddingExpense}
            className="flex flex-[2] items-center justify-center gap-2 rounded-xl bg-zinc-900 py-3 text-xs font-bold tracking-wider text-white uppercase transition-opacity hover:opacity-90 disabled:opacity-50 dark:bg-zinc-100 dark:text-zinc-900"
          >
            {isAddingExpense && (
              <div className="h-3 w-3 animate-spin rounded-full border-2 border-white/30 border-t-white" />
            )}
            {editingExpenseId ? 'Update Record' : 'Add Record'}
          </button>
        </div>
      </form>
    </div>
  );
}
